import React from "react";
import { ArrowPathIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useTheme } from "../../contexts/ThemeContext";

export const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("ru-RU"));

export const Card = ({ title, subtitle, actions, children, className = "", bodyClassName = "" }) => (
  <section className={`overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm ${className}`}>
    {(title || actions) && (
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-primary px-5 py-3.5">
        <div className="min-w-0">
          {title && <h3 className="text-sm font-semibold text-text-primary">{title}</h3>}
          {subtitle && <p className="text-xs text-text-muted">{subtitle}</p>}
        </div>
        {actions}
      </div>
    )}
    <div className={bodyClassName}>{children}</div>
  </section>
);

export const Kpi = ({ label, value, hint, icon: Icon, tone = "bg-indigo-500/15 text-indigo-500" }) => (
  <div className="rounded-block border border-border-primary bg-bg-card p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-medium text-text-secondary">{label}</p>
        <p className="mt-1 text-2xl font-bold tabular-nums text-text-primary">{typeof value === "number" ? fmt(value) : value ?? "—"}</p>
        {hint && <p className="mt-0.5 truncate text-xs text-text-muted">{hint}</p>}
      </div>
      {Icon && (
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}`}>
          <Icon className="h-5 w-5" />
        </span>
      )}
    </div>
  </div>
);

export const KpiSkeleton = ({ count = 4 }) => (
  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="h-[92px] animate-pulse rounded-block border border-border-primary bg-bg-card" />
    ))}
  </div>
);

export const ErrorState = ({ text = "Не удалось загрузить данные", detail, onRetry }) => (
  <div className="flex flex-col items-center gap-3 rounded-block border border-border-primary bg-bg-card px-6 py-14 text-center">
    <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
    <div>
      <p className="text-sm text-text-primary">{text}</p>
      {detail && <p className="mt-1 text-xs text-text-muted">{detail}</p>}
    </div>
    {onRetry && (
      <button type="button" onClick={onRetry} className="inline-flex items-center gap-2 rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
        <ArrowPathIcon className="h-4 w-4" /> Повторить
      </button>
    )}
  </div>
);

export const Empty = ({ children = "Нет данных" }) => <p className="px-5 py-10 text-center text-sm text-text-muted">{children}</p>;

export const RefreshBtn = ({ onClick, loading }) => (
  <button type="button" onClick={onClick} disabled={loading} title="Обновить" aria-label="Обновить" className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary disabled:opacity-60">
    <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
  </button>
);

export const Segmented = ({ options, value, onChange }) => (
  <div role="tablist" className="flex rounded-lg border border-border-primary bg-bg-card p-0.5">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="tab"
        aria-selected={value === o.value}
        onClick={() => onChange(o.value)}
        className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${value === o.value ? "bg-accent-grad text-white" : "text-text-secondary hover:text-text-primary"}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/** Ранжированный список с полосками: [{ label, value, sub? }] */
export const RankList = ({ items, max, emptyText, color = "bg-accent-grad" }) => {
  if (!items.length) return <Empty>{emptyText}</Empty>;
  const top = max ?? Math.max(...items.map((i) => i.value), 1);
  return (
    <ol className="space-y-2.5 p-5">
      {items.map((it, i) => (
        <li key={`${it.label}-${i}`}>
          <div className="mb-1 flex items-center gap-2 text-sm">
            <span className={`w-5 shrink-0 text-center text-xs font-semibold tabular-nums ${i < 3 ? "text-[#7C3AED]" : "text-text-muted"}`}>{i + 1}</span>
            <span className="min-w-0 flex-1 truncate text-text-primary" title={it.label}>
              {it.label}
            </span>
            {it.sub && <span className="shrink-0 text-xs text-text-muted">{it.sub}</span>}
            <span className="shrink-0 font-semibold tabular-nums text-text-primary">{fmt(it.value)}</span>
          </div>
          <div className="ml-7 h-1.5 overflow-hidden rounded-full bg-bg-tertiary">
            <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max((it.value / top) * 100, it.value > 0 ? 1 : 0)}%` }} />
          </div>
        </li>
      ))}
    </ol>
  );
};

export const CHART_COLORS = ["#6366F1", "#14B8A6", "#F59E0B", "#EC4899", "#3B82F6", "#8B5CF6", "#22C55E", "#EF4444", "#64748B", "#D97706"];

/** Цвета осей/сетки/тултипа recharts под текущую тему */
export const useChartTheme = () => {
  const { theme } = useTheme();
  const dark = theme === "dark";
  return {
    grid: dark ? "#2a2f36" : "#e5e7eb",
    axis: { fontSize: 11, fill: "#9CA3AF" },
    tooltip: {
      contentStyle: { background: dark ? "#1c2127" : "#fff", border: `1px solid ${dark ? "#2a2f36" : "#e5e7eb"}`, borderRadius: 8, fontSize: 12 },
      labelStyle: { color: dark ? "#e5e7eb" : "#111827" },
      cursor: { fill: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" },
    },
  };
};

const ymd = (d) => d.toISOString().slice(0, 10);

/** Период → { start_date, end_date } в формате YYYY-MM-DD (как принимает сервис мониторинга).
 * end_date — завтра, чтобы сегодняшний день точно попал в выборку. */
export const periodRange = (days) => {
  if (!days) return {};
  const now = new Date();
  return { start_date: ymd(new Date(now.getTime() - days * 86400000)), end_date: ymd(new Date(now.getTime() + 86400000)) };
};

export const PERIODS = [
  { value: 7, label: "7 дней" },
  { value: 30, label: "30 дней" },
  { value: 90, label: "90 дней" },
];
